import { useTranslation } from 'react-i18next';
import { endOfWeek, startOfWeek } from 'date-fns';
import { dateLocales } from '@/i18n/dateLocales';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';
import WeekView, { WeekViewFlags } from '@/components/calendar/weekly/WeekView';
import WeekNavLabel from '@/components/calendar/weekly/WeekNavLabel';
import { themeToWeekTokens } from '@/components/calendar/weekly/weekTokens';
import { usePendingStarValues } from '@/components/activity/usePendingStarValues';

/**
 * TeenWeek — tunn wrapper runt den gemensamma WeekView. Teens additiva lager
 * (hopfällbara dagar, veckoframsteg, guldstjärna, status-dots, glow) läggs på via
 * flaggor. Färger via det gemensamma tema-tokensystemet (getThemeTokens). Teen är
 * låst till innevarande vecka (statisk WeekNavLabel, ingen bläddring).
 */
const TEEN_FLAGS: WeekViewFlags = {
  collapsibleDays: true,
  showDayCount: true,
  showWeekProgress: true,
  showDayStar: true,
  showStatusDots: true,
  showActivityEmoji: false,
  showCompanion: false,
  todayGlow: true,
};

const TeenWeek = () => {
  const { t, i18n } = useTranslation();
  const { selectedTheme, isDarkMode } = useThemePreference();
  // Väntande stjärnvärden är teen-specifika, så frågan ställs här i wrappern och inte
  // i den gemensamma WeekView (barn/adult har inget att hämta).
  const { isPending: isStarValuePending } = usePendingStarValues();

  const locale = dateLocales[i18n.language.split('-')[0]] ?? dateLocales.en;
  const tokens = themeToWeekTokens(getThemeTokens(selectedTheme, isDarkMode), isDarkMode);

  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

  return (
    <WeekView
      title={t('teen.week.title')}
      weekStart={weekStart}
      tokens={tokens}
      flags={TEEN_FLAGS}
      weekNav={<WeekNavLabel weekStart={weekStart} weekEnd={weekEnd} tokens={tokens} />}
      todayLabel={t('teen.week.today')}
      noActivitiesLabel={t('teen.week.noActivities')}
      weekProgressLabel={t('teen.week.weekProgress')}
      nowLabel={t('week.now')}
      locale={locale}
      isStarValuePending={isStarValuePending}
      starValuePendingLabel={t('starValue.pendingBadge') as string}
    />
  );
};

export default TeenWeek;
